import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ImpersonationService } from '../services/impersonation.service';
import { environment } from '../../../environments/environment';
import { AUTH_ENTRY_ROUTE, isPreAuthUrl } from '../auth-entry';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const isApiRequest = req.url.startsWith(environment.apiUrl);
  if (isApiRequest && !req.withCredentials) {
    req = req.clone({ withCredentials: true });
  }

  const skipEndpoints = [
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/auth/device/verify'
  ];
  if (skipEndpoints.some(endpoint => req.url.includes(endpoint))) {
    return next(req);
  }

  // SUDO MODE: Inject impersonation header if active
  const impersonationService = inject(ImpersonationService);
  const impersonatedUserId = impersonationService.getImpersonatedUserId();

  if (impersonatedUserId) {
    req = req.clone({
      setHeaders: {
        'x-impersonated-user-id': impersonatedUserId
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get a 401 error (unauthorized), try to refresh the token
      if (error.status === 401) {
        return authService.refreshSession().pipe(
          switchMap(() => {
            // After refreshing the token, retry the original request
            return next(req);
          }),
          catchError((refreshError) => {
            // If refresh fails, redirect to the pre-auth entry (tenant picker) with the
            // current URL as returnUrl. But don't redirect if we're already on a pre-auth
            // page (e.g. /auth/device, /auth/login, /tenant) to avoid redirect loops from
            // APP_INITIALIZER's loadUser() call.
            const currentUrl = router.url;
            if (!isPreAuthUrl(currentUrl)) {
              router.navigate([AUTH_ENTRY_ROUTE], {
                queryParams: { returnUrl: currentUrl }
              });
            }
            return throwError(() => refreshError);
          })
        );
      }
      return throwError(() => error);
    })
  );
};
